import React, { useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';

/**
 * ProtectedRoute component that checks for specific permissions
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 * @param {string|Array<string>} props.requiredPermission - Permission(s) required to access the route
 * @param {boolean} props.requireAll - If true, user must have ALL permissions. If false, user needs ANY permission. Default: false
 */
const PermissionProtectedRoute = ({ children, requiredPermission, requireAll = false }) => {
  const auth = useContext(AuthContext);
  const location = useLocation();
  
  // If not logged in, redirect to login
  if (!auth.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If no permission required, just check if user is logged in
  if (!requiredPermission) {
    return children;
  }
  
  // Check permissions
  const permissions = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
  
  let hasAccess = false;
  
  // Admin users have access to all pages
  if (auth.hasPermission('admin')) {
    hasAccess = true;
  } else if (requireAll) {
    // User must have ALL permissions
    hasAccess = permissions.every(permission => auth.hasPermission(permission));
  } else {
    // User must have ANY of the permissions
    hasAccess = permissions.some(permission => auth.hasPermission(permission));
  }
  
  if (!hasAccess) {
    // Redirect to dashboard with access denied message
    return (
      <Navigate 
        to="/" 
        state={{ 
          from: location, 
          error: 'Access denied. You do not have the required permissions to access this page.' 
        }} 
        replace 
      />
    );
  }
  
  return children;
};

export default PermissionProtectedRoute;
